import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';

// Components
import Navbar from './components/Navbar';
import Login from './components/Login';
import Register from './components/Register';
import StudentDashboard from './components/student/StudentDashboard';
import TestDetails from './components/student/TestDetails';
import RequestGraderRole from './components/student/RequestGraderRole';
import AdminDashboard from './components/admin/AdminDashboard';
import CreateTest from './components/admin/CreateTest';
import EditTest from './components/admin/EditTest';
import GradeSubmissions from './components/admin/GradeSubmissions';
import UserManagement from './components/admin/UserManagement';
import ManageMocks from './components/admin/ManageMocks';
import PermissionManagement from './components/admin/PermissionManagement';
import CreateAccount from './components/admin/CreateAccount';
import TeacherAssignments from './components/admin/TeacherAssignments';
import GraderDashboard from './components/grader/GraderDashboard';
import CombinedDashboard from './components/CombinedDashboard';
import TeacherDashboard from './components/teacher/TeacherDashboard';
import Profile from './components/Profile';
import LandingPage from './components/LandingPage';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Set default axios base URL and token interceptor
axios.defaults.baseURL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001';

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppContent />
        </div>
      </Router>
    </AuthProvider>
  );
};

const AppContent = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" />} />
        <Route path="/register" element={!user ? <Register /> : <Navigate to="/dashboard" />} />
        
        {/* Landing Page - Public facing */}
        <Route path="/" element={
          user ? (
            (user.role === 'admin' || user.role === 'super-admin') ? <AdminDashboard /> :
            user.role === 'teacher' ? <TeacherDashboard /> :
            <CombinedDashboard />
          ) : (
            <LandingPage />
          )
        } />
        
        {/* Dedicated Dashboard Route - Available to all authenticated users */}
        <Route path="/dashboard" element={
          user ? <CombinedDashboard /> : <Navigate to="/login" />
        } />
        
        {/* Student Routes */}
        <Route path="/test/:id" element={
          user && (user.role === 'student' || user.role === 'grader') ? <TestDetails /> : <Navigate to="/login" />
        } />
        <Route path="/request-grader" element={
          user && user.role === 'student' ? <RequestGraderRole /> : <Navigate to="/login" />
        } />
        
        {/* Grader Routes */}
        <Route path="/grader" element={
          user && user.role === 'grader' ? <GradeSubmissions /> : <Navigate to="/login" />
        } />
        <Route path="/grader/grade/:testId" element={
          user && (user.role === 'grader' || user.role === 'admin') ? <GradeSubmissions /> : <Navigate to="/login" />
        } />
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          user && (user.role === 'admin' || user.role === 'super-admin') ? <AdminDashboard /> : <Navigate to="/login" />
        } />
        <Route path="/admin/create-test" element={
          user && (user.role === 'admin' || user.role === 'super-admin') ? <CreateTest /> : <Navigate to="/login" />
        } />
        <Route path="/admin/edit-test/:id" element={
          user && (user.role === 'admin' || user.role === 'super-admin') ? <EditTest /> : <Navigate to="/login" />
        } />
        <Route path="/admin/grade/:testId" element={
          user && (user.role === 'admin' || user.role === 'grader' || user.role === 'super-admin') ? <GradeSubmissions /> : <Navigate to="/login" />
        } />
        <Route path="/admin/users" element={
          user && (user.role === 'admin' || user.role === 'super-admin') ? <UserManagement /> : <Navigate to="/login" />
        } />
        <Route path="/admin/manage-mocks" element={
          user && (user.role === 'admin' || user.role === 'super-admin') ? <ManageMocks /> : <Navigate to="/login" />
        } />
        <Route path="/admin/permissions" element={
          user && (user.role === 'admin' || user.role === 'super-admin') ? <PermissionManagement /> : <Navigate to="/login" />
        } />
        <Route path="/admin/create-account" element={
          user && (user.role === 'admin' || user.role === 'super-admin') ? <CreateAccount /> : <Navigate to="/login" />
        } />
        <Route path="/admin/teacher-assignments" element={
          user && (user.role === 'admin' || user.role === 'super-admin') ? <TeacherAssignments /> : <Navigate to="/login" />
        } />
        
        {/* Shared Grading Routes */}
        <Route path="/grade/:testId" element={
          user && (user.role === 'admin' || user.role === 'grader' || user.role === 'super-admin') ? <GradeSubmissions /> : <Navigate to="/login" />
        } />
        
        {/* Profile Route - Available to all authenticated users */}
        <Route path="/profile" element={
          user ? <Profile /> : <Navigate to="/login" />
        } />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
};

export default App;
